import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { SpecDetail, StorySummary } from '../../types/project.js';
import * as projectApi from '../../services/project-api.js';
import { router } from '../../services/router.js';

interface KanbanColumn {
  key: string;
  label: string;
  stories: StorySummary[];
}

@customElement('cp-spec-detail')
export class CpSpecDetail extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) projectId = 0;
  @property({ type: String }) specId = '';

  @state() private _spec: SpecDetail | null = null;
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _expandedStoryId: string | null = null;

  updated(changed: Map<string, unknown>): void {
    if ((changed.has('projectId') || changed.has('specId')) && this.projectId > 0 && this.specId) {
      void this._loadSpec();
    }
  }

  private async _loadSpec(): Promise<void> {
    this._loading = true;
    this._error = '';

    try {
      this._spec = await projectApi.getSpecDetail(this.projectId, this.specId);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Spec konnte nicht geladen werden';
    } finally {
      this._loading = false;
    }
  }

  private _goBack(): void {
    router.navigateTo(`/projects/${this.projectId}`);
  }

  private _toggleStory(storyId: string): void {
    this._expandedStoryId = this._expandedStoryId === storyId ? null : storyId;
  }

  private get _columns(): KanbanColumn[] {
    const stories = this._spec?.stories ?? [];
    const columnDefs: { key: string; label: string; match: (s: string) => boolean }[] = [
      { key: 'ready', label: 'Ready', match: s => s === 'ready' },
      { key: 'in-progress', label: 'In Progress', match: s => s === 'in_progress' || s === 'in-progress' },
      { key: 'done', label: 'Done', match: s => s === 'done' },
      { key: 'blocked', label: 'Blocked', match: s => s === 'blocked' },
    ];

    return columnDefs.map(col => ({
      key: col.key,
      label: col.label,
      stories: stories.filter(s => col.match(s.status)),
    }));
  }

  private _typeBadgeClass(type: string): string {
    switch (type) {
      case 'frontend': return 'cp-spec-detail__type-badge--frontend';
      case 'backend': return 'cp-spec-detail__type-badge--backend';
      case 'fullstack': return 'cp-spec-detail__type-badge--fullstack';
      case 'system': return 'cp-spec-detail__type-badge--system';
      default: return '';
    }
  }

  private _renderEffort(effort: number) {
    return html`
      <span class="cp-spec-detail__effort" title="Effort: ${effort}">
        ${'●'.repeat(Math.min(effort, 5))}${'○'.repeat(Math.max(0, 5 - effort))}
      </span>
    `;
  }

  private _renderStoryCard(story: StorySummary) {
    const isExpanded = this._expandedStoryId === story.id;

    return html`
      <div
        class="cp-spec-detail__card ${isExpanded ? 'cp-spec-detail__card--expanded' : ''}"
        @click=${() => this._toggleStory(story.id)}
      >
        <div class="cp-spec-detail__card-header">
          <span class="cp-spec-detail__card-id">${story.id}</span>
          <span class="cp-spec-detail__type-badge ${this._typeBadgeClass(story.type)}">${story.type}</span>
        </div>
        <p class="cp-spec-detail__card-title">${story.title}</p>
        <div class="cp-spec-detail__card-meta">
          ${this._renderEffort(story.effort)}
          <span class="cp-spec-detail__card-priority">${story.priority}</span>
        </div>
        ${isExpanded ? this._renderStoryDetails(story) : nothing}
      </div>
    `;
  }

  private _renderStoryDetails(story: StorySummary) {
    return html`
      <div class="cp-spec-detail__card-details">
        <div class="cp-spec-detail__detail-row">
          <span class="cp-spec-detail__detail-label">Status:</span>
          <span class="cp-spec-detail__detail-value">${story.status}</span>
        </div>
        <div class="cp-spec-detail__detail-row">
          <span class="cp-spec-detail__detail-label">Priority:</span>
          <span class="cp-spec-detail__detail-value">${story.priority}</span>
        </div>
        <div class="cp-spec-detail__detail-row">
          <span class="cp-spec-detail__detail-label">Effort:</span>
          <span class="cp-spec-detail__detail-value">${story.effort}</span>
        </div>
        ${story.dependencies.length > 0 ? html`
          <div class="cp-spec-detail__detail-row">
            <span class="cp-spec-detail__detail-label">Dependencies:</span>
            <span class="cp-spec-detail__detail-value">${story.dependencies.join(', ')}</span>
          </div>
        ` : nothing}
      </div>
    `;
  }

  private _renderColumn(col: KanbanColumn) {
    return html`
      <div class="cp-spec-detail__column">
        <div class="cp-spec-detail__column-header">
          <span class="cp-spec-detail__column-title">${col.label}</span>
          <span class="cp-spec-detail__column-count">${col.stories.length}</span>
        </div>
        <div class="cp-spec-detail__column-body">
          ${col.stories.length > 0
            ? col.stories.map(s => this._renderStoryCard(s))
            : html`<p class="cp-spec-detail__column-empty">Keine Stories</p>`
          }
        </div>
      </div>
    `;
  }

  private _renderSkeleton() {
    return html`
      <div class="cp-spec-detail__board">
        ${[1, 2, 3, 4].map(() => html`
          <div class="cp-spec-detail__column">
            <div class="cp-spec-detail__column-header">
              <div class="cp-spec-detail__skeleton-line cp-spec-detail__skeleton-line--title"></div>
            </div>
            <div class="cp-spec-detail__column-body">
              <div class="cp-spec-detail__skeleton-card"></div>
              <div class="cp-spec-detail__skeleton-card"></div>
            </div>
          </div>
        `)}
      </div>
    `;
  }

  private _renderEmpty() {
    return html`
      <div class="cp-spec-detail__empty">
        <p class="cp-spec-detail__empty-text">Keine Stories vorhanden</p>
      </div>
    `;
  }

  render() {
    return html`
      <div class="cp-spec-detail">
        <div class="cp-spec-detail__header">
          <button class="cp-spec-detail__back" @click=${this._goBack}>
            &#8592; Zurueck
          </button>
          ${this._spec ? html`
            <h2 class="cp-spec-detail__title">${this._spec.name}</h2>
            <div class="cp-spec-detail__progress">
              <div class="cp-spec-detail__progress-bar">
                <div
                  class="cp-spec-detail__progress-fill"
                  style="width: ${this._spec.progressPercent}%"
                ></div>
              </div>
              <span class="cp-spec-detail__progress-text">
                ${this._spec.storyCounts.done}/${this._spec.storyCounts.total} Stories (${this._spec.progressPercent}%)
              </span>
            </div>
          ` : nothing}
        </div>

        ${this._loading ? this._renderSkeleton() : nothing}

        ${this._error ? html`
          <p class="cp-spec-detail__error">${this._error}</p>
        ` : nothing}

        ${!this._loading && !this._error && this._spec ? html`
          ${this._spec.stories.length === 0
            ? this._renderEmpty()
            : html`
              <div class="cp-spec-detail__board">
                ${this._columns.map(col => this._renderColumn(col))}
              </div>
            `
          }
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-spec-detail': CpSpecDetail;
  }
}
